import { useState } from "react";
import {
  Container,
  Card,
  Table,
  Badge,
  Text,
  ActionIcon,
  Group,
  TextInput,
  Tooltip,
  ScrollArea,
  Button,
  Modal,
  Stack,
  FileInput,
  SegmentedControl,
  Select,
  Progress,
  Tabs,
} from "@mantine/core";
import {
  IconUpload,
  IconSearch,
  IconExternalLink,
  IconLink,
  IconTrash,
  IconLock,
  IconWorld,
  IconFiles,
  IconKey,
} from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { filesApi } from "../lib/api";
import { FileVisibility, StoredFile, UserRole } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { PageTransition } from "../components/PageTransition";
import { EmptyState } from "../components/EmptyState";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { Pagination } from "../components/Pagination";
import { FileApiKeysPanel } from "../components/FileApiKeysPanel";
import { showSuccess, showError } from "../lib/notifications";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

function UploadModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState<FileVisibility>("PRIVATE");
  const [namespace, setNamespace] = useState("console");
  const [progress, setProgress] = useState(0);

  const close = () => {
    setFile(null);
    setName("");
    setProgress(0);
    onClose();
  };

  const uploadMutation = useMutation({
    mutationFn: () =>
      filesApi.upload(
        file!,
        { visibility, namespace: namespace || undefined, filename: name },
        setProgress
      ),
    onSuccess: () => {
      showSuccess(`${name.trim() || file?.name} uploaded.`);
      queryClient.invalidateQueries({ queryKey: ["files"] });
      queryClient.invalidateQueries({ queryKey: ["file-namespaces"] });
      close();
    },
    onError: (error) => {
      setProgress(0);
      showError(error);
    },
  });

  return (
    <Modal opened={opened} onClose={close} title="Upload file">
      <Stack>
        <FileInput
          label="File"
          value={file}
          onChange={(picked) => {
            setFile(picked);
            setName(picked?.name ?? "");
          }}
          icon={<IconUpload size={16} />}
          disabled={uploadMutation.isLoading}
        />
        <TextInput
          label="Name"
          description="Shown in the console and used as the download filename."
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          maxLength={255}
          disabled={!file || uploadMutation.isLoading}
        />
        <div>
          <Text size="sm" weight={500} mb={4}>
            Visibility
          </Text>
          <SegmentedControl
            fullWidth
            value={visibility}
            onChange={(v) => setVisibility(v as FileVisibility)}
            disabled={uploadMutation.isLoading}
            data={[
              { label: "Private", value: "PRIVATE" },
              { label: "Public (CDN)", value: "PUBLIC" },
            ]}
          />
          <Text size="xs" color="dimmed" mt={4}>
            {visibility === "PRIVATE"
              ? "Only reachable through the console or an authenticated service, via links that expire in minutes."
              : "Served by the CDN at a permanent URL anyone can open."}
          </Text>
        </div>
        <TextInput
          label="Namespace"
          description="The app this file belongs to."
          value={namespace}
          onChange={(e) => setNamespace(e.currentTarget.value.toLowerCase())}
          disabled={uploadMutation.isLoading}
        />
        {uploadMutation.isLoading && <Progress value={progress} animate />}
        <Group position="right">
          <Button variant="default" onClick={close} disabled={uploadMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => uploadMutation.mutate()}
            disabled={!file || !name.trim()}
            loading={uploadMutation.isLoading}
          >
            Upload
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function FilesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSuperuser = user?.role === UserRole.SUPERUSER;
  const tab = isSuperuser && location.pathname.endsWith("/api-keys") ? "api-keys" : "files";
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <Container fluid p={0} pos="relative">
      <PageTransition>
        <PageHeader
          title="Files"
          description="Upload and share files. Private files are reachable only through the console or authenticated services."
        >
          {tab === "files" && (
            <Button leftIcon={<IconUpload size={16} />} onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
          )}
        </PageHeader>

        {isSuperuser ? (
          <Tabs
            value={tab}
            onTabChange={(value) => navigate(value === "api-keys" ? "/files/api-keys" : "/files")}
          >
            <Tabs.List>
              <Tabs.Tab value="files" icon={<IconFiles size={14} />}>
                Files
              </Tabs.Tab>
              <Tabs.Tab value="api-keys" icon={<IconKey size={14} />}>
                API Keys
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="files">
              <FilesList />
            </Tabs.Panel>
            <Tabs.Panel value="api-keys">
              <FileApiKeysPanel />
            </Tabs.Panel>
          </Tabs>
        ) : (
          <FilesList />
        )}
      </PageTransition>

      <UploadModal opened={uploadOpen} onClose={() => setUploadOpen(false)} />
    </Container>
  );
}

function FilesList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPERUSER;

  const [search, setSearch] = useState("");
  const [namespace, setNamespace] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [deleteTarget, setDeleteTarget] = useState<StoredFile | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["files", { search, namespace, visibility, page, pageSize }],
    queryFn: async () =>
      (
        await filesApi.list({
          search: search || undefined,
          namespace: namespace || undefined,
          visibility: (visibility as FileVisibility) || undefined,
          page,
          pageSize,
        })
      ).data,
    keepPreviousData: true,
  });

  const { data: namespaces = [] } = useQuery({
    queryKey: ["file-namespaces"],
    queryFn: async () => (await filesApi.namespaces()).data.namespaces,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => filesApi.delete(id),
    onSuccess: () => {
      showSuccess("File deleted.");
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["files"] });
    },
    onError: (error) => showError(error),
  });

  const open = async (file: StoredFile) => {
    // Open the tab synchronously so popup blockers allow it, then point it at the URL.
    const tab = window.open("", "_blank");
    try {
      const { data } = await filesApi.get(file.id, true);
      if (tab) tab.location.href = data.url;
    } catch (error) {
      tab?.close();
      showError(error);
    }
  };

  const copyLink = async (file: StoredFile) => {
    try {
      const { data } = await filesApi.get(file.id);
      await navigator.clipboard.writeText(data.url);
      showSuccess(
        data.expiresAt
          ? `Private link copied. It expires ${formatDistanceToNow(new Date(data.expiresAt), { addSuffix: true })}.`
          : "Public CDN link copied."
      );
    } catch (error) {
      showError(error);
    }
  };

  const items = data?.items ?? [];
  const filtering = !!(search || namespace || visibility);

  return (
    <>
        {isLoading ? (
          <LoadingOverlay message="Loading..." />
        ) : items.length === 0 && !filtering ? (
          <EmptyState title="No files yet" description="Uploaded files will show up here." />
        ) : (
          <Card mt="md" shadow="sm" radius="md" withBorder>
            <Group p="md" spacing="sm">
              <TextInput
                placeholder="Search by name..."
                icon={<IconSearch size={16} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
                sx={(theme) => ({
                  width: 300,
                  [theme.fn.smallerThan("sm")]: { width: "100%" },
                })}
              />
              <Select
                placeholder="All namespaces"
                clearable
                data={namespaces}
                value={namespace}
                onChange={(v) => {
                  setNamespace(v);
                  setPage(1);
                }}
              />
              <Select
                placeholder="Any visibility"
                clearable
                data={[
                  { label: "Private", value: "PRIVATE" },
                  { label: "Public", value: "PUBLIC" },
                ]}
                value={visibility}
                onChange={(v) => {
                  setVisibility(v);
                  setPage(1);
                }}
              />
            </Group>

            <ScrollArea>
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Namespace</th>
                    <th>Visibility</th>
                    <th>Size</th>
                    <th>Uploaded by</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((file) => (
                    <tr key={file.id}>
                      <td style={{ maxWidth: 320 }}>
                        <Text size="sm" weight={500} truncate title={file.filename}>
                          {file.filename}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {file.contentType}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm">{file.namespace}</Text>
                      </td>
                      <td>
                        {file.visibility === "PRIVATE" ? (
                          <Badge color="gray" leftSection={<IconLock size={10} />}>
                            private
                          </Badge>
                        ) : (
                          <Badge color="teal" leftSection={<IconWorld size={10} />}>
                            public
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Text size="sm">{formatSize(file.size)}</Text>
                      </td>
                      <td>
                        <Text size="sm">{file.uploadedBy || "—"}</Text>
                      </td>
                      <td>
                        <Text size="sm" color="dimmed">
                          {formatDistanceToNow(new Date(file.createdAt), { addSuffix: true })}
                        </Text>
                      </td>
                      <td>
                        <Group spacing={4} noWrap>
                          <Tooltip label="Open">
                            <ActionIcon variant="subtle" onClick={() => open(file)}>
                              <IconExternalLink size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={file.visibility === "PRIVATE" ? "Copy expiring link" : "Copy CDN link"}>
                            <ActionIcon variant="subtle" onClick={() => copyLink(file)}>
                              <IconLink size={16} />
                            </ActionIcon>
                          </Tooltip>
                          {(isAdmin || file.uploadedById === user?.id) && (
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => setDeleteTarget(file)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <Text size="sm" color="dimmed" align="center" py="md">
                          No files match these filters.
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </ScrollArea>

            <Group px="md" pb="md">
              <div style={{ width: "100%" }}>
                <Pagination
                  total={data?.total ?? 0}
                  page={page}
                  onPageChange={setPage}
                  limit={pageSize}
                  onLimitChange={(limit) => {
                    setPageSize(limit);
                    setPage(1);
                  }}
                />
              </div>
            </Group>
          </Card>
        )}

      <ConfirmationModal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete file"
        message={`This permanently deletes "${deleteTarget?.filename}" from storage. Links to it will stop working. This can't be undone.`}
        confirmLabel="Delete"
        isLoading={deleteMutation.isLoading}
      />
    </>
  );
}
